import {prop, invertObj} from 'ramda'
import {buildOptionalFilters} from '../business/optionalFilters'

import {
  accessControlRules,
  ALLOW,
  DENY,
  ALLOW_TOKEN,
  DENY_TOKEN
} from './constants'
import {
  AccessControlAction,
  AccessControlRule,
  FetchAccessControlParams,
  Hits,
  Response
} from '../types'

interface GetAccessControlRuleResponse {
  hits: Hits[]
  rule: AccessControlRule
}

/**
 * Returns the matched access control action and rule
 * by fetching ALLOW and DENY lists and the user score
 * @param params - user payload and algolia client
 * @param action - access control action code
 */
export const getAccessControlRule = async (
  params: FetchAccessControlParams,
  action: AccessControlAction
): Promise<GetAccessControlRuleResponse> => {
  const optionalFilters = buildOptionalFilters(
    accessControlRules,
    params.payload
  )

  const actionMapper = {
    [ALLOW_TOKEN]: ALLOW,
    [DENY_TOKEN]: DENY
  }

  try {
    const {hits}: Response = await params.algoliaClient.search({
      query: actionMapper[action],
      optionalFilters,
      hitsPerPage: 1,
      getRankingInfo: true,
      attributesToHighlight: null,
      attributesToRetrieve: [
        'action',
        'objectID',
        'optionalFiltersScore',
        'optionalFiltersScoreOffset'
      ]
    })

    const rule = hits.find(
      ({
        optionalFiltersScore,
        optionalFiltersScoreOffset,
        _rankingInfo
      }: Hits) =>
        optionalFiltersScore + optionalFiltersScoreOffset ===
        _rankingInfo.filters
    )

    const actionToken = prop(
      rule?.action,
      invertObj(actionMapper)
    ) as AccessControlAction

    const tokenMatchesAction = actionToken === action
    const matchedRule = {
      rule: {
        action: actionToken,
        ruleId: rule?.objectID
      }
    }

    return {
      ...(tokenMatchesAction && matchedRule),
      hits
    }
  } catch {
    return {rule: {action: null, ruleId: null}, hits: []}
  }
}
